/**
 * Performance Table
 * Generic table for displaying performance breakdowns
 */
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { getRoiColor } from '@utils/colorUtils';
import type { ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

interface Column {
  key: string;
  label: string;
  format?: 'percent' | 'roi' | 'number' | 'units' | 'statLabel';
  width?: string;
}

// biome-ignore lint/suspicious/noExplicitAny: generic table accepts dynamic data shapes
type PerformanceData = Record<string, any>;

interface PerformanceTableProps {
  title: string;
  data: PerformanceData[];
  columns: Column[];
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatValue(value: unknown, format?: string): string {
  if (value === null || value === undefined) return '-';

  if (format === 'percent') {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    return `${(num * 100).toFixed(1)}%`;
  }

  if (format === 'roi') {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(1)}%`;
  }

  if (format === 'number') {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    return num.toLocaleString();
  }

  if (format === 'statLabel') {
    return formatStatLabel(String(value));
  }

  if (format === 'units') {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}u`;
  }

  return String(value);
}

function getUnitsColor(value: unknown): string {
  if (typeof value !== 'number') return 'text-theme-text-secondary';
  if (value > 2) return 'text-theme-success';
  if (value > 0) return 'text-green-300';
  if (value > -2) return 'text-theme-warning';
  return 'text-theme-danger';
}

function getHitRateColor(value: unknown): string {
  if (typeof value !== 'number') return 'text-theme-text-secondary';
  const percent = value * 100;
  if (percent >= 55) return 'text-theme-success';
  if (percent >= 52) return 'text-green-300';
  if (percent >= 48) return 'text-theme-text-secondary';
  if (percent >= 45) return 'text-theme-warning';
  return 'text-theme-danger';
}

// =============================================================================
// Component
// =============================================================================

export function PerformanceTable({ title, data, columns }: PerformanceTableProps): ReactElement {
  if (data.length === 0) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-theme-text-secondary mb-3">{title}</h3>
        <p className="text-theme-text-tertiary text-sm">No data available</p>
      </div>
    );
  }

  return (
    <div className="bg-theme-surface/50 rounded-lg p-4 flex flex-col max-h-80">
      <h3 className="text-sm font-medium text-theme-text-secondary mb-3 shrink-0">{title}</h3>
      <div className="overflow-auto flex-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-theme-text-secondary text-left border-b border-theme-border">
              {columns.map((col) => (
                <th key={col.key} className={`pb-2 font-medium ${col.width || ''}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx} className="border-b border-theme-border/50 last:border-0">
                {columns.map((col) => {
                  const value = row[col.key];
                  const formatted = formatValue(value, col.format);
                  let colorClass = 'text-theme-text-secondary';

                  if (col.format === 'roi') {
                    colorClass =
                      typeof value === 'number' ? getRoiColor(value) : 'text-theme-text-secondary';
                  } else if (col.format === 'units') {
                    colorClass = getUnitsColor(value);
                  } else if (
                    col.format === 'percent' &&
                    (col.key.toLowerCase().includes('hit') || col.key.toLowerCase().includes('win'))
                  ) {
                    colorClass = getHitRateColor(value);
                  }

                  return (
                    <td key={col.key} className={`py-2 ${col.width || ''} ${colorClass}`}>
                      {col.key === columns[0].key ? (
                        <span className="font-medium text-theme-text">{formatted}</span>
                      ) : (
                        formatted
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
